import { Box } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState } from 'react';

import { DashboardLayout } from '#modules/user/features/dashboard/components/DashboardLayout';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { BottomNavigation } from '#features/layout/components/BottomNavigation';
import { HeaderView } from '#features/layout/components/Header';
import { NotificationsCenterView } from '#features/notifications/components/NotificationsCenter';
import type { NotificationsTab } from '#features/notifications/model/notifications';

const states = [
  'normal',
  'mixed',
  'empty',
  'read',
  'loading',
  'error',
  'history-error',
  'more-error',
  'action-error',
] as const;
type State = (typeof states)[number];
export const Route = createFileRoute('/{-$locale}/_layout/preview/notifications')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    state: states.includes(search.state as State) ? (search.state as State) : ('normal' as State),
  }),
  component: PreviewRoute,
});
function PreviewRoute() {
  const search = Route.useSearch();
  return <Preview key={`${search.lang}:${search.state}`} {...search} />;
}
function Preview({ lang, state }: { lang: 'ru' | 'en'; state: State }) {
  const [i18n] = useState(() =>
    setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } })
  );
  const [tab, setTab] = useState<NotificationsTab>('unread');
  const [client] = useState(() => {
    let rows = Array.from({ length: state === 'empty' ? 0 : 45 }, (_, index) => ({
      id: `preview-notification-${index}`,
      count: index === 4 ? 3 : 1,
      isRead:
        state === 'read' ||
        state === 'history-error' ||
        (state === 'mixed' ? index % 3 === 1 : index > 28),
      updatedAt: new Date(Date.now() - index * 3600000).toISOString(),
      userData:
        index % 3
          ? {
              displayName: 'Alex_with_a_very_long_display_name',
              userId: 'preview-alex',
              avatar: null,
            }
          : null,
      message:
        index % 3 === 0
          ? lang === 'ru'
            ? 'Вашу заявку по Arc Raiders снова можно поднять в поиске. <a href="/posts/11111111-1111-4111-8111-111111111111">Открыть заявку</a>'
            : 'Your Arc Raiders listing can be bumped again. <a href="/posts/11111111-1111-4111-8111-111111111111">Open listing</a>'
          : lang === 'ru'
            ? '<a href="/players/alex">Alex_with_a_very_long_display_name</a> пригласил вас в <a href="/posts/11111111-1111-4111-8111-111111111111?session=preview">вечернюю сессию с друзьями в Arc Raiders</a>'
            : '<a href="/players/alex">Alex_with_a_very_long_display_name</a> invited you to <a href="/posts/11111111-1111-4111-8111-111111111111?session=preview">an evening Arc Raiders session with friends</a>',
    }));
    const cache = new QueryClient({ defaultOptions: { queries: { retry: false } } });
    const queryOptions = cache.defaultQueryOptions.bind(cache);
    const mutationOptions = cache.defaultMutationOptions.bind(cache);
    // This private cache never calls the API; actions only mutate the fixture in memory.
    cache.defaultQueryOptions = ((options: Parameters<QueryClient['defaultQueryOptions']>[0]) => {
      const key = options.queryKey ?? [];
      const isNotification = key[0] === 'notifications' || key[0] === 'hasUnreadNotifications';
      return {
        ...queryOptions(options),
        enabled: isNotification && state !== 'loading' && options.enabled !== false,
        retry: false,
        queryFn: async (context: { pageParam?: unknown }) => {
          if (state === 'error') throw new Error('Preview failure');
          if (key[0] === 'hasUnreadNotifications') return rows.some((row) => !row.isRead);
          if (key[1] !== 'history') {
            if (state === 'history-error') throw new Error('Preview history check failure');
            const params = key[1] as { limit?: number; filters?: { field: string }[] };
            const list = params?.filters?.some((filter) => filter.field === 'isRead')
              ? rows.filter((row) => !row.isRead)
              : rows;
            return list.slice(0, params?.limit ?? 1);
          }
          const offset = Number(context.pageParam ?? 0);
          if (state === 'more-error' && offset > 0) throw new Error('Preview next page failure');
          const list = key[2] === 'unread' ? rows.filter((row) => !row.isRead) : rows;
          return {
            notifications: list.slice(offset, offset + 20),
            hasMore: offset + 20 < list.length,
            nextOffset: Math.min(offset + 20, list.length),
          };
        },
      };
    }) as QueryClient['defaultQueryOptions'];
    cache.defaultMutationOptions = (options) => ({
      ...mutationOptions(options),
      mutationFn: async () => {
        if (state === 'action-error')
          throw new Error(
            lang === 'ru' ? 'Не удалось выполнить действие' : 'Could not complete this action'
          );
        if (options?.mutationKey?.[0] === 'notification-read-all')
          rows = rows.map((row) => ({ ...row, isRead: true }));
        if (options?.mutationKey?.[0] === 'notification-delete-all') rows = [];
        return { __typename: 'Operation', status: 'OK' };
      },
    });
    cache.setQueryData(['me'], { id: 'preview-me', displayName: 'Player', pushToken: [] });
    return cache;
  });
  return (
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={client}>
        <Fixture tab={tab} onTabChange={setTab} />
      </QueryClientProvider>
    </I18nProvider>
  );
}
function Fixture({
  tab,
  onTabChange,
}: {
  tab: NotificationsTab;
  onTabChange: (tab: NotificationsTab) => void;
}) {
  const { t } = useLingui();
  return (
    <Box bg="bg" minH="100dvh">
      <HeaderView
        isBrowser
        isAuth
        isLanding={false}
        mobile={{
          screen: { kind: 'detail', entity: 'notifications' },
          title: t`notifications.title`,
          previousStep: false,
          backToPost: false,
          back: () => {},
          cancel: () => {},
        }}
      />
      <Box px={{ base: 3, lg: 5 }} py={5} pb={{ base: '96px', md: 5 }}>
        <DashboardLayout isAuth>
          <NotificationsCenterView tab={tab} onTabChange={onTabChange} onBack={() => {}} />
        </DashboardLayout>
      </Box>
      <BottomNavigation />
    </Box>
  );
}
